import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface ServiceArea {
  id: string
  label: string
  tableNumberFrom: number
  tableNumberTo: number
  receptionAccount: string
  paymentEnabled: boolean
  restaurantId: string
  createdAt?: string
  updatedAt?: string
}

export interface ServiceAreaSettings {
  serviceLastName: boolean
  serviceLastNameRequired: boolean
  serviceRoomNo: boolean
  serviceRoomNoRequired: boolean
  serviceRoomNoOffset: number
  servicePaymentCallStatus: boolean
  paymentButtonLabel: string
  serviceCallStatus: boolean
  serviceCallOptions: string
  paymentSectionStatus: boolean
  paymentSectionOptions: string
}

export interface ServiceAreaDraft extends Omit<ServiceArea, 'id' | 'createdAt' | 'updatedAt'> {
  validationError?: string
}

const createEmptyDraft = (): ServiceAreaDraft => ({
  label: '',
  tableNumberFrom: 1,
  tableNumberTo: 10,
  receptionAccount: '',
  paymentEnabled: false,
  restaurantId: '',
  validationError: '',
})

const createEmptySettings = (): ServiceAreaSettings => ({
  serviceLastName: false,
  serviceLastNameRequired: false,
  serviceRoomNo: false,
  serviceRoomNoRequired: false,
  serviceRoomNoOffset: 0,
  servicePaymentCallStatus: false,
  paymentButtonLabel: '',
  serviceCallStatus: false,
  serviceCallOptions: '',
  paymentSectionStatus: false,
  paymentSectionOptions: '',
})

export const useServiceAreas = (getRestaurantId: () => string) => {
  const serviceAreas = ref<ServiceArea[]>([])
  const settings = ref<ServiceAreaSettings>(createEmptySettings())
  const draft = ref<ServiceAreaDraft>(createEmptyDraft())
  const receptionAccounts = ref<string[]>([])

  const initialize = () => {
    serviceAreas.value = []
    settings.value = createEmptySettings()
    clearDraft()
  }

  const clearDraft = () => {
    draft.value = createEmptyDraft()
    draft.value.restaurantId = getRestaurantId()
  }

  const initializeDraft = (area?: ServiceArea) => {
    if (area) {
      draft.value = {
        label: area.label,
        tableNumberFrom: area.tableNumberFrom,
        tableNumberTo: area.tableNumberTo,
        receptionAccount: area.receptionAccount,
        paymentEnabled: area.paymentEnabled,
        restaurantId: area.restaurantId,
        validationError: '',
      }
    } else {
      clearDraft()
    }
  }

  const updateDraft = (updates: Partial<ServiceAreaDraft>) => {
    draft.value = {
      ...draft.value,
      ...updates,
    }
  }

  const getDraft = (): ServiceAreaDraft => draft.value

  const loadReceptionAccounts = async (): Promise<boolean> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.warn('⚠️ Restaurant ID is not available for loading reception accounts')
      return false
    }

    try {

      const response = await apiInstance.get(`/restaurant/${restaurantId}/reception`)
      const data = response.data

      if (data.error === false && Array.isArray(data.data)) {
        receptionAccounts.value = data.data.map((account: any) => account.email || account.name || '')
        return true
      } else {
        console.warn('⚠️ Failed to load reception accounts:', data.message || 'No data returned')
        return false
      }
    } catch (error: any) {
      console.error('🔴 Error loading reception accounts:', error.message)
      console.error('🔴 Error response:', JSON.stringify(error.response?.data, null, 2))
      return false
    }
  }

  const loadServiceAreas = async (): Promise<boolean> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.warn('⚠️ Restaurant ID is not available for loading service areas')
      return false
    }

    try {

      const response = await apiInstance.get(`/restaurant/${restaurantId}/service-area`)
      const data = response.data

      if (data.error === false && Array.isArray(data.data)) {
        // Extract service areas
        serviceAreas.value = data.data.map((area: any) => ({
          id: String(area.id),
          label: area.name || '',
          tableNumberFrom: parseInt(area.table_no_from, 10) || 1,
          tableNumberTo: parseInt(area.table_no_to, 10) || 10,
          receptionAccount: String(area.reception_id || '0'),
          paymentEnabled: area.payment_status === 1 || area.payment_status === true,
          restaurantId: restaurantId,
          isWarning: area.is_warning == 1 ? true : false
        }))

        return true
      } else {
        console.warn('⚠️ Failed to load service areas:', data.message || 'No data returned')
        return false
      }
    } catch (error: any) {
      console.error('🔴 Error loading service areas:', error.message)
      console.error('🔴 Error response:', JSON.stringify(error.response?.data, null, 2))
      return false
    }
  }

  const loadServiceAreaSettings = async (): Promise<boolean> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.warn('⚠️ Restaurant ID is not available for loading service area settings')
      return false
    }

    try {

      const response = await apiInstance.get(`/restaurant/${restaurantId}/service-area-settings`)
      const data = response.data

      if (data.error === false && data.data) {
        // Extract service area settings
        settings.value = {
          serviceLastName: data.data.service_last_name === 1 || data.data.service_last_name === true,
          serviceLastNameRequired: data.data.service_last_name_required === 1 || data.data.service_last_name_required === true,
          serviceRoomNo: data.data.service_room_no === 1 || data.data.service_room_no === true,
          serviceRoomNoRequired: data.data.service_room_no_required === 1 || data.data.service_room_no_required === true,
          serviceRoomNoOffset: parseInt(data.data.service_offset || '0', 10),
          servicePaymentCallStatus: data.data.service_payment_call_status === 1 || data.data.service_payment_call_status === true,
          paymentButtonLabel: data.data.payment_button_label || '',
          serviceCallStatus: data.data.service_call_status === 1 || data.data.service_call_status === true,
          serviceCallOptions: data.data.service_call_options || '',
          paymentSectionStatus: data.data.payment_section_status === 1 || data.data.payment_section_status === true,
          paymentSectionOptions: data.data.payment_section_options || '',
        }

        return true
      } else {
        console.warn('⚠️ Failed to load service area settings:', data.message || 'No data returned')
        return false
      }
    } catch (error: any) {
      console.error('🔴 Error loading service area settings:', error.message)
      console.error('🔴 Error response:', JSON.stringify(error.response?.data, null, 2))
      return false
    }
  }

  const createServiceArea = async (areaData: Omit<ServiceArea, 'id' | 'createdAt' | 'updatedAt'>): Promise<{ success: boolean; data?: ServiceArea; errors?: any }> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for creating service area')
      return { success: false, errors: { general: ['Restaurant ID is not available'] } }
    }

    try {
      const payload: any = {
        table_no_from: areaData.tableNumberFrom,
        table_no_to: areaData.tableNumberTo,
        name: areaData.label, // Map label to name
        reception_id: areaData.receptionAccount, // Map receptionAccount to reception_id
        payment_status: areaData.paymentEnabled ? 1 : 0, // Map paymentEnabled to payment_status (1 for true, 0 for false)
      }

      const response = await apiInstance.post(`/restaurant/${restaurantId}/service-area`, payload)
      const responseData = response.data

      if (responseData.error === false && responseData.data) {
        const newArea = responseData.data
        const createdArea: ServiceArea = {
          id: String(newArea.id),
          label: newArea.name || '', // Map name back to label for internal use
          tableNumberFrom: parseInt(newArea.table_no_from, 10) || 1,
          tableNumberTo: parseInt(newArea.table_no_to, 10) || 10,
          receptionAccount: newArea.reception_id || '',
          paymentEnabled: newArea.payment_status === 1 || newArea.payment_status === true,
          restaurantId: restaurantId,
          createdAt: newArea.created_at,
          updatedAt: newArea.updated_at,
        }
        serviceAreas.value.push(createdArea)
        return { success: true, data: createdArea }
      }

      if (responseData.errors) {
        console.warn('⚠️ Validation errors:', responseData.errors)
        return { success: false, errors: responseData.errors }
      }

      console.warn('⚠️ Create response indicates failure:', responseData.message)
      return { success: false, errors: { general: [responseData.message] } }
    } catch (error: any) {
      console.error('🔴 Error creating service area:', error.message)
      console.error('🔴 Error status:', error.response?.status)
      console.error('🔴 Error response data:', JSON.stringify(error.response?.data, null, 2))

      if (error.response?.status === 422) {
        const responseData = error.response?.data
        if (responseData?.errors) {
          return { success: false, errors: responseData.errors }
        }
        if (responseData?.message) {
          return { success: false, errors: { general: [responseData.message] } }
        }
      }

      if (error.response?.data?.errors) {
        return { success: false, errors: error.response.data.errors }
      }

      if (error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      return { success: false, errors: { general: [error.message || 'Failed to create service area'] } }
    }
  }

  const updateServiceArea = async (areaId: string | number, areaData: Omit<ServiceArea, 'id' | 'createdAt' | 'updatedAt'>): Promise<{ success: boolean; data?: ServiceArea; errors?: any }> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for updating service area')
      return { success: false, errors: { general: ['Restaurant ID is not available'] } }
    }

    try {
      const payload: any = {
        table_no_from: areaData.tableNumberFrom,
        table_no_to: areaData.tableNumberTo,
        name: areaData.label,
        reception_id: areaData.receptionAccount,
        payment_status: areaData.paymentEnabled ? 1 : 0,
      }

      const response = await apiInstance.put(`/restaurant/${restaurantId}/service-area/${areaId}`, payload)
      const responseData = response.data

      if (responseData.error === false && responseData.data) {
        const updatedData = responseData.data
        const updatedArea: ServiceArea = {
          id: String(areaId),
          label: updatedData.name || '',
          tableNumberFrom: parseInt(updatedData.table_no_from, 10) || 1,
          tableNumberTo: parseInt(updatedData.table_no_to, 10) || 10,
          receptionAccount: String(updatedData.reception_id || '0'),
          paymentEnabled: updatedData.payment_status === 1 || updatedData.payment_status === true,
          restaurantId: restaurantId,
        }
        const index = serviceAreas.value.findIndex((a) => a.id === String(areaId))
        if (index !== -1) {
          serviceAreas.value[index] = updatedArea
        }
        return { success: true, data: updatedArea }
      }

      if (responseData.errors) {
        console.warn('⚠️ Validation errors:', responseData.errors)
        return { success: false, errors: responseData.errors }
      }

      console.warn('⚠️ Update response indicates failure:', responseData.message)
      return { success: false, errors: { general: [responseData.message] } }
    } catch (error: any) {
      console.error('🔴 Error updating service area:', error.message)
      console.error('🔴 Error status:', error.response?.status)
      console.error('🔴 Error response data:', JSON.stringify(error.response?.data, null, 2))

      if (error.response?.status === 422) {
        const responseData = error.response?.data
        if (responseData?.errors) {
          return { success: false, errors: responseData.errors }
        }
        if (responseData?.message) {
          return { success: false, errors: { general: [responseData.message] } }
        }
      }

      if (error.response?.data?.errors) {
        return { success: false, errors: error.response.data.errors }
      }

      if (error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      return { success: false, errors: { general: [error.message || 'Failed to update service area'] } }
    }
  }

  const deleteServiceArea = async (areaId: string | number): Promise<{ success: boolean; errors?: any }> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for deleting service area')
      return { success: false, errors: { general: ['Restaurant ID is not available'] } }
    }

    try {

      const response = await apiInstance.delete(`/restaurant/${restaurantId}/service-area/${areaId}`)
      const responseData = response.data

      if (responseData.error === false || response.status === 200 || response.status === 204) {
        serviceAreas.value = serviceAreas.value.filter((a) => a.id !== String(areaId))
        return { success: true }
      }

      console.warn('⚠️ Delete response indicates failure:', responseData.message)
      return { success: false, errors: { general: [responseData.message] } }
    } catch (error: any) {
      console.error('🔴 Error deleting service area:', error.message)
      console.error('🔴 Error response:', JSON.stringify(error.response?.data, null, 2))

      if (error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      return { success: false, errors: { general: [error.message || 'Failed to delete service area'] } }
    }
  }

  const getRestaurantServiceAreas = (restaurantId: string) => {
    return serviceAreas.value.filter((area) => area.restaurantId === restaurantId)
  }

  const getServiceAreaById = (id: string) => {
    return serviceAreas.value.find((area) => area.id === id)
  }

  const saveServiceAreaSettings = async (newSettings: ServiceAreaSettings): Promise<{ success: boolean; errors?: any }> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for saving service area settings')
      return { success: false, errors: { general: ['Restaurant ID is not available'] } }
    }

    try {
      const payload = {
        service_last_name: newSettings.serviceLastName ? 1 : 0,
        service_last_name_required: newSettings.serviceLastNameRequired ? 1 : 0,
        service_room_no: newSettings.serviceRoomNo ? 1 : 0,
        service_room_no_required: newSettings.serviceRoomNoRequired ? 1 : 0,
        service_offset: newSettings.serviceRoomNoOffset,
        service_payment_call_status: newSettings.servicePaymentCallStatus ? 1 : 0,
        payment_button_label: newSettings.paymentButtonLabel || '',
        service_call_status: newSettings.serviceCallStatus ? 1 : 0,
        service_call_options: newSettings.serviceCallOptions || '',
        payment_section_status: newSettings.paymentSectionStatus ? 1 : 0,
        payment_section_options: newSettings.paymentSectionOptions || '',
      }

      const response = await apiInstance.put(`/restaurant/${restaurantId}/service-area-settings`, payload)
      const responseData = response.data

      if (responseData.error === false) {
        // Update the store with the new settings
        settings.value = newSettings
        return { success: true }
      }

      if (responseData.errors) {
        console.warn('⚠️ Validation errors:', responseData.errors)
        return { success: false, errors: responseData.errors }
      }

      console.warn('⚠️ Save response indicates failure:', responseData.message)
      return { success: false, errors: { general: [responseData.message] } }
    } catch (error: any) {
      console.error('🔴 Error saving service area settings:', error.message)
      console.error('🔴 Error status:', error.response?.status)
      console.error('🔴 Error response data:', JSON.stringify(error.response?.data, null, 2))

      if (error.response?.data?.errors) {
        return { success: false, errors: error.response.data.errors }
      }

      if (error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      return { success: false, errors: { general: [error.message || 'Failed to save service area settings'] } }
    }
  }

  return {
    serviceAreas,
    settings,
    draft,
    receptionAccounts,
    initialize,
    clearDraft,
    initializeDraft,
    updateDraft,
    getDraft,
    loadReceptionAccounts,
    loadServiceAreas,
    loadServiceAreaSettings,
    createServiceArea,
    updateServiceArea,
    deleteServiceArea,
    saveServiceAreaSettings,
    getRestaurantServiceAreas,
    getServiceAreaById,
  }
}
